/**
 * ZipCodeSelector Component
 * 
 * AI-styled dropdown selector for Arizona ZIP codes with search functionality
 * Includes data availability indicators and city grouping
 */

import React, { useState, useMemo, useEffect } from 'react';
import Select from 'react-select';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMapMarkerAlt, faCircle, faSearch, faSpinner } from '@fortawesome/free-solid-svg-icons';
import axiosInstance from '../../../helpers/api';

// Import types
import type { ZipCodeSelectorProps, ZipCodeOption } from '../../../../types/marketIntelligence.types';

// Extended Arizona ZIP codes with cities and data availability
const ARIZONA_ZIP_CODES: ZipCodeOption[] = [
  // Phoenix Metro Area
  { value: '85001', label: '85001 - Phoenix Downtown', city: 'Phoenix', hasData: true },
  { value: '85002', label: '85002 - Phoenix', city: 'Phoenix', hasData: true },
  { value: '85003', label: '85003 - Phoenix', city: 'Phoenix', hasData: true },
  { value: '85004', label: '85004 - Phoenix', city: 'Phoenix', hasData: true },
  { value: '85006', label: '85006 - Phoenix', city: 'Phoenix', hasData: true },
  { value: '85007', label: '85007 - Phoenix', city: 'Phoenix', hasData: true },
  { value: '85008', label: '85008 - Phoenix', city: 'Phoenix', hasData: true },
  { value: '85009', label: '85009 - Phoenix', city: 'Phoenix', hasData: true },
  { value: '85012', label: '85012 - Phoenix', city: 'Phoenix', hasData: true },
  { value: '85013', label: '85013 - Phoenix', city: 'Phoenix', hasData: true },
  { value: '85014', label: '85014 - Phoenix', city: 'Phoenix', hasData: true },
  { value: '85015', label: '85015 - Phoenix', city: 'Phoenix', hasData: true },
  { value: '85016', label: '85016 - Phoenix', city: 'Phoenix', hasData: true },
  { value: '85017', label: '85017 - Phoenix', city: 'Phoenix', hasData: true },
  { value: '85018', label: '85018 - Phoenix', city: 'Phoenix', hasData: true },
  { value: '85019', label: '85019 - Phoenix', city: 'Phoenix', hasData: true },
  { value: '85020', label: '85020 - Phoenix', city: 'Phoenix', hasData: true },
  { value: '85021', label: '85021 - Phoenix', city: 'Phoenix', hasData: true },
  { value: '85022', label: '85022 - Phoenix', city: 'Phoenix', hasData: true },
  { value: '85023', label: '85023 - Phoenix', city: 'Phoenix', hasData: true },
  { value: '85024', label: '85024 - Phoenix', city: 'Phoenix', hasData: true },
  { value: '85027', label: '85027 - Phoenix', city: 'Phoenix', hasData: true },
  { value: '85028', label: '85028 - Phoenix', city: 'Phoenix', hasData: true },
  { value: '85029', label: '85029 - Phoenix', city: 'Phoenix', hasData: true },
  { value: '85031', label: '85031 - Phoenix', city: 'Phoenix', hasData: true },
  { value: '85032', label: '85032 - Phoenix', city: 'Phoenix', hasData: true },
  { value: '85033', label: '85033 - Phoenix', city: 'Phoenix', hasData: true },
  { value: '85034', label: '85034 - Phoenix', city: 'Phoenix', hasData: true },
  { value: '85035', label: '85035 - Phoenix', city: 'Phoenix', hasData: true },
  { value: '85037', label: '85037 - Phoenix', city: 'Phoenix', hasData: true },
  { value: '85040', label: '85040 - Phoenix', city: 'Phoenix', hasData: true },
  { value: '85041', label: '85041 - Phoenix', city: 'Phoenix', hasData: true },
  { value: '85042', label: '85042 - Phoenix', city: 'Phoenix', hasData: true },
  { value: '85043', label: '85043 - Phoenix', city: 'Phoenix', hasData: true },
  { value: '85044', label: '85044 - Phoenix', city: 'Phoenix', hasData: true },
  { value: '85045', label: '85045 - Phoenix', city: 'Phoenix', hasData: true },
  { value: '85048', label: '85048 - Phoenix', city: 'Phoenix', hasData: true },
  { value: '85050', label: '85050 - Phoenix', city: 'Phoenix', hasData: true },
  { value: '85051', label: '85051 - Phoenix', city: 'Phoenix', hasData: true },
  { value: '85053', label: '85053 - Phoenix', city: 'Phoenix', hasData: true },
  { value: '85054', label: '85054 - Phoenix', city: 'Phoenix', hasData: true },
  { value: '85083', label: '85083 - Phoenix', city: 'Phoenix', hasData: true },
  { value: '85085', label: '85085 - Phoenix', city: 'Phoenix', hasData: true },

  // Scottsdale
  { value: '85250', label: '85250 - Scottsdale', city: 'Scottsdale', hasData: true },
  { value: '85251', label: '85251 - Scottsdale', city: 'Scottsdale', hasData: true },
  { value: '85252', label: '85252 - Scottsdale', city: 'Scottsdale', hasData: true },
  { value: '85253', label: '85253 - Scottsdale', city: 'Scottsdale', hasData: true },
  { value: '85254', label: '85254 - Scottsdale', city: 'Scottsdale', hasData: true },
  { value: '85255', label: '85255 - Paradise Valley', city: 'Paradise Valley', hasData: true },
  { value: '85256', label: '85256 - Scottsdale', city: 'Scottsdale', hasData: true },
  { value: '85257', label: '85257 - Scottsdale', city: 'Scottsdale', hasData: true },
  { value: '85258', label: '85258 - Scottsdale', city: 'Scottsdale', hasData: true },
  { value: '85259', label: '85259 - Scottsdale', city: 'Scottsdale', hasData: true },
  { value: '85260', label: '85260 - Scottsdale', city: 'Scottsdale', hasData: true },
  { value: '85261', label: '85261 - Scottsdale', city: 'Scottsdale', hasData: true },
  { value: '85262', label: '85262 - Scottsdale', city: 'Scottsdale', hasData: true },
  { value: '85263', label: '85263 - Scottsdale', city: 'Scottsdale', hasData: true },
  { value: '85264', label: '85264 - Scottsdale', city: 'Scottsdale', hasData: true },
  { value: '85266', label: '85266 - Scottsdale', city: 'Scottsdale', hasData: true },
  { value: '85267', label: '85267 - Scottsdale', city: 'Scottsdale', hasData: true },
  { value: '85268', label: '85268 - Scottsdale', city: 'Scottsdale', hasData: true },

  // Mesa
  { value: '85201', label: '85201 - Mesa', city: 'Mesa', hasData: true },
  { value: '85202', label: '85202 - Mesa', city: 'Mesa', hasData: true },
  { value: '85203', label: '85203 - Mesa', city: 'Mesa', hasData: true },
  { value: '85204', label: '85204 - Mesa', city: 'Mesa', hasData: true },
  { value: '85205', label: '85205 - Mesa', city: 'Mesa', hasData: true },
  { value: '85206', label: '85206 - Mesa', city: 'Mesa', hasData: true },
  { value: '85207', label: '85207 - Mesa', city: 'Mesa', hasData: true },
  { value: '85208', label: '85208 - Mesa', city: 'Mesa', hasData: true },
  { value: '85209', label: '85209 - Mesa', city: 'Mesa', hasData: true },
  { value: '85210', label: '85210 - Mesa', city: 'Mesa', hasData: true },
  { value: '85212', label: '85212 - Mesa', city: 'Mesa', hasData: true },
  { value: '85213', label: '85213 - Mesa', city: 'Mesa', hasData: true },
  { value: '85215', label: '85215 - Mesa', city: 'Mesa', hasData: true },
  { value: '85224', label: '85224 - Chandler', city: 'Chandler', hasData: true },
  { value: '85225', label: '85225 - Chandler', city: 'Chandler', hasData: true },
  { value: '85226', label: '85226 - Chandler', city: 'Chandler', hasData: true },
  { value: '85248', label: '85248 - Chandler', city: 'Chandler', hasData: true },
  { value: '85249', label: '85249 - Chandler', city: 'Chandler', hasData: true },
  { value: '85286', label: '85286 - Chandler', city: 'Chandler', hasData: true },
  { value: '85295', label: '85295 - Gilbert', city: 'Gilbert', hasData: true },
  { value: '85296', label: '85296 - Gilbert', city: 'Gilbert', hasData: true },
  { value: '85297', label: '85297 - Gilbert', city: 'Gilbert', hasData: true },
  { value: '85298', label: '85298 - Gilbert', city: 'Gilbert', hasData: true },

  // Tempe
  { value: '85281', label: '85281 - Tempe', city: 'Tempe', hasData: true },
  { value: '85282', label: '85282 - Tempe', city: 'Tempe', hasData: true },
  { value: '85283', label: '85283 - Tempe', city: 'Tempe', hasData: true },
  { value: '85284', label: '85284 - Tempe', city: 'Tempe', hasData: true },
  { value: '85287', label: '85287 - Tempe', city: 'Tempe', hasData: true },

  // Glendale & Peoria
  { value: '85301', label: '85301 - Glendale', city: 'Glendale', hasData: true },
  { value: '85302', label: '85302 - Glendale', city: 'Glendale', hasData: true },
  { value: '85303', label: '85303 - Glendale', city: 'Glendale', hasData: true },
  { value: '85304', label: '85304 - Glendale', city: 'Glendale', hasData: true },
  { value: '85305', label: '85305 - Glendale', city: 'Glendale', hasData: true },
  { value: '85306', label: '85306 - Glendale', city: 'Glendale', hasData: true },
  { value: '85307', label: '85307 - Glendale', city: 'Glendale', hasData: true },
  { value: '85308', label: '85308 - Glendale', city: 'Glendale', hasData: true },
  { value: '85309', label: '85309 - Glendale', city: 'Glendale', hasData: true },
  { value: '85310', label: '85310 - Glendale', city: 'Glendale', hasData: true },
  { value: '85331', label: '85331 - Cave Creek', city: 'Cave Creek', hasData: true },
  { value: '85335', label: '85335 - El Mirage', city: 'El Mirage', hasData: true },
  { value: '85338', label: '85338 - Peoria', city: 'Peoria', hasData: true },
  { value: '85339', label: '85339 - Peoria', city: 'Peoria', hasData: true },
  { value: '85340', label: '85340 - Litchfield Park', city: 'Litchfield Park', hasData: true },
  { value: '85342', label: '85342 - Peoria', city: 'Peoria', hasData: true },
  { value: '85345', label: '85345 - Peoria', city: 'Peoria', hasData: true },
  { value: '85351', label: '85351 - Surprise', city: 'Surprise', hasData: true },
  { value: '85374', label: '85374 - Surprise', city: 'Surprise', hasData: true },
  { value: '85375', label: '85375 - Surprise', city: 'Surprise', hasData: true },
  { value: '85379', label: '85379 - Surprise', city: 'Surprise', hasData: true },
  { value: '85383', label: '85383 - Peoria', city: 'Peoria', hasData: true },
  { value: '85387', label: '85387 - Peoria', city: 'Peoria', hasData: true },
  { value: '85388', label: '85388 - Peoria', city: 'Peoria', hasData: true },
  { value: '85395', label: '85395 - Peoria', city: 'Peoria', hasData: true },

  // West Valley
  { value: '85323', label: '85323 - Avondale', city: 'Avondale', hasData: true },
  { value: '85353', label: '85353 - Avondale', city: 'Avondale', hasData: true },
  { value: '85354', label: '85354 - Avondale', city: 'Avondale', hasData: true },
  { value: '85355', label: '85355 - Avondale', city: 'Avondale', hasData: true },
  { value: '85361', label: '85361 - Goodyear', city: 'Goodyear', hasData: true },
  { value: '85338', label: '85338 - Goodyear', city: 'Goodyear', hasData: true },
  { value: '85395', label: '85395 - Goodyear', city: 'Goodyear', hasData: true },

  // Tucson Metro
  { value: '85701', label: '85701 - Tucson', city: 'Tucson', hasData: true },
  { value: '85702', label: '85702 - Tucson', city: 'Tucson', hasData: true },
  { value: '85703', label: '85703 - Tucson', city: 'Tucson', hasData: true },
  { value: '85704', label: '85704 - Tucson', city: 'Tucson', hasData: true },
  { value: '85705', label: '85705 - Tucson', city: 'Tucson', hasData: true },
  { value: '85706', label: '85706 - Tucson', city: 'Tucson', hasData: true },
  { value: '85707', label: '85707 - Tucson', city: 'Tucson', hasData: true },
  { value: '85708', label: '85708 - Tucson', city: 'Tucson', hasData: true },
  { value: '85709', label: '85709 - Tucson', city: 'Tucson', hasData: true },
  { value: '85710', label: '85710 - Tucson', city: 'Tucson', hasData: true },
  { value: '85711', label: '85711 - Tucson', city: 'Tucson', hasData: true },
  { value: '85712', label: '85712 - Tucson', city: 'Tucson', hasData: true },
  { value: '85713', label: '85713 - Tucson', city: 'Tucson', hasData: true },
  { value: '85714', label: '85714 - Tucson', city: 'Tucson', hasData: true },
  { value: '85715', label: '85715 - Tucson', city: 'Tucson', hasData: true },
  { value: '85716', label: '85716 - Tucson', city: 'Tucson', hasData: true },
  { value: '85718', label: '85718 - Tucson', city: 'Tucson', hasData: true },
  { value: '85719', label: '85719 - Tucson', city: 'Tucson', hasData: true },
  { value: '85721', label: '85721 - Tucson', city: 'Tucson', hasData: true },
  { value: '85724', label: '85724 - Tucson', city: 'Tucson', hasData: true },
  { value: '85730', label: '85730 - Tucson', city: 'Tucson', hasData: true },
  { value: '85734', label: '85734 - Tucson', city: 'Tucson', hasData: true },
  { value: '85737', label: '85737 - Tucson', city: 'Tucson', hasData: true },
  { value: '85741', label: '85741 - Tucson', city: 'Tucson', hasData: true },
  { value: '85742', label: '85742 - Tucson', city: 'Tucson', hasData: true },
  { value: '85743', label: '85743 - Tucson', city: 'Tucson', hasData: true },
  { value: '85745', label: '85745 - Tucson', city: 'Tucson', hasData: true },
  { value: '85746', label: '85746 - Tucson', city: 'Tucson', hasData: true },
  { value: '85747', label: '85747 - Tucson', city: 'Tucson', hasData: true },
  { value: '85748', label: '85748 - Tucson', city: 'Tucson', hasData: true },
  { value: '85749', label: '85749 - Tucson', city: 'Tucson', hasData: true },
  { value: '85750', label: '85750 - Tucson', city: 'Tucson', hasData: true },
  { value: '85751', label: '85751 - Tucson', city: 'Tucson', hasData: true },
  { value: '85754', label: '85754 - Tucson', city: 'Tucson', hasData: true },
  { value: '85755', label: '85755 - Tucson', city: 'Tucson', hasData: true },
  { value: '85756', label: '85756 - Tucson', city: 'Tucson', hasData: true },
  { value: '85757', label: '85757 - Tucson', city: 'Tucson', hasData: true },
  { value: '85775', label: '85775 - Oro Valley', city: 'Oro Valley', hasData: true },

  // Other Arizona Cities
  { value: '86001', label: '86001 - Flagstaff', city: 'Flagstaff', hasData: false },
  { value: '86004', label: '86004 - Flagstaff', city: 'Flagstaff', hasData: false },
  { value: '86301', label: '86301 - Prescott', city: 'Prescott', hasData: false },
  { value: '86303', label: '86303 - Prescott', city: 'Prescott', hasData: false },
  { value: '86305', label: '86305 - Prescott Valley', city: 'Prescott Valley', hasData: false },
  { value: '86314', label: '86314 - Prescott Valley', city: 'Prescott Valley', hasData: false },
  { value: '86401', label: '86401 - Kingman', city: 'Kingman', hasData: false },
  { value: '86403', label: '86403 - Kingman', city: 'Kingman', hasData: false },
  { value: '86404', label: '86404 - Kingman', city: 'Kingman', hasData: false },
  { value: '86409', label: '86409 - Kingman', city: 'Kingman', hasData: false },
  { value: '86413', label: '86413 - Kingman', city: 'Kingman', hasData: false },
];

// Custom option component for react-select
const CustomOption = ({ data, ...props }: any) => {
  return (
    <div 
      {...props.innerProps}
      className={`d-flex align-items-center justify-content-between p-2 ${props.isFocused ? 'bg-light' : ''}`}
      style={{ 
        cursor: 'pointer',
        borderRadius: '8px',
        margin: '2px'
      }}
    >
      <div className="d-flex align-items-center">
        <FontAwesomeIcon 
          icon={faMapMarkerAlt} 
          className="me-2 text-muted" 
          style={{ fontSize: '12px' }} 
        />
        <div>
          <div style={{ fontSize: '14px', fontWeight: '500' }}>
            {data.value} - {data.city}
          </div>
          <div style={{ fontSize: '12px' }} className="text-muted">
            {data.city}, Arizona
          </div>
        </div>
      </div>
      
      {/* Data availability indicator */}
      <div className="d-flex align-items-center">
        <FontAwesomeIcon
          icon={faCircle}
          style={{
            fontSize: '8px',
            color: data.hasData ? '#10b981' : '#6b7280'
          }}
        />
        <span 
          style={{ 
            fontSize: '10px',
            marginLeft: '4px',
            color: data.hasData ? '#10b981' : '#6b7280'
          }}
        >
          {data.hasData ? 'Data Available' : 'Limited Data'}
        </span>
      </div>
    </div>
  );
};

// Custom single value component
const CustomSingleValue = ({ data }: any) => {
  return (
    <div className="d-flex align-items-center">
      <FontAwesomeIcon 
        icon={faMapMarkerAlt} 
        className="me-2" 
        style={{ color: '#00d4ff', fontSize: '14px' }} 
      />
      <span style={{ fontWeight: '500' }}>
        {data.value} - {data.city}
      </span>
    </div>
  );
};

const ZipCodeSelector: React.FC<ZipCodeSelectorProps> = ({
  value,
  onChange,
  arizonaZipCodes = [],
  disabled = false,
  placeholder = 'Search Arizona ZIP codes...',
  showDataAvailability = true
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [zipCodes, setZipCodes] = useState<ZipCodeOption[]>(arizonaZipCodes);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch ZIP codes from database on component mount
  useEffect(() => {
    const fetchZipCodes = async () => {
      if (arizonaZipCodes.length > 0) {
        // Use provided ZIP codes if available
        setZipCodes(arizonaZipCodes);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        
        console.log('🔍 Fetching ZIP codes from API...');
        const response = await axiosInstance.get('/market-intelligence/zip-codes');
        console.log('📊 API Response:', response.data);
        
        if (response.data.success && response.data.data && response.data.data.zip_codes) {
          console.log(`✅ Found ${response.data.data.zip_codes.length} ZIP codes`);
          setZipCodes(response.data.data.zip_codes);
        } else {
          console.error('❌ Invalid response format:', response.data);
          throw new Error('Invalid response format');
        }
      } catch (err: any) {
        console.error('❌ Error fetching ZIP codes:', err);
        console.error('📍 API endpoint:', '/market-intelligence/zip-codes');
        console.error('🔧 Using fallback ZIP codes');
        setError('Failed to load ZIP codes from database');
        
        // Fallback to all available ZIP codes with data mapping from your processing script
        const knownDataZips = ['85001', '85375', '85331', '85138', '85262', '85043', '85335', '85382', 
                               '85250', '85251', '85252', '85253', '85254', '85255', '85256', '85257', 
                               '85258', '85259', '85260', '85261', '85201', '85202', '85203', '85204', 
                               '85205', '85206', '85207', '85208', '85209', '85210', '85281', '85282', 
                               '85283', '85284', '85301', '85302', '85303', '85304', '85305', '85306'];
        
        const fallbackZipCodes = ARIZONA_ZIP_CODES.map(zip => ({
          ...zip,
          hasData: knownDataZips.includes(zip.value),
        }));
        
        console.log(`📋 Using ${fallbackZipCodes.length} fallback ZIP codes`);
        console.log(`📊 ZIP codes with data: ${fallbackZipCodes.filter(z => z.hasData).length}`);
        setZipCodes(fallbackZipCodes);
      } finally {
        setLoading(false);
      }
    };

    fetchZipCodes();
  }, [arizonaZipCodes]);

  // Group ZIP codes by city for better organization
  const groupedOptions = useMemo(() => {
    const grouped: { [key: string]: ZipCodeOption[] } = {};
    
    zipCodes.forEach(zip => {
      const city = zip.city || 'Other';
      if (!grouped[city]) {
        grouped[city] = [];
      }
      grouped[city].push(zip);
    });

    // Sort cities and ZIP codes within each city
    return Object.keys(grouped)
      .sort()
      .map(city => ({
        label: city,
        options: grouped[city].sort((a, b) => a.value.localeCompare(b.value))
      }));
  }, [zipCodes]);

  // Filter options based on search term
  const filteredOptions = useMemo(() => {
    if (!searchTerm) return groupedOptions;

    return groupedOptions
      .map(group => ({
        ...group,
        options: group.options.filter(option =>
          option.value.includes(searchTerm) ||
          option.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (option.city && option.city.toLowerCase().includes(searchTerm.toLowerCase()))
        )
      }))
      .filter(group => group.options.length > 0);
  }, [groupedOptions, searchTerm]);

  // Find selected option
  const selectedOption = zipCodes.find(zip => zip.value === value);

  // Custom styles for react-select with AI theme
  const customStyles = {
    control: (provided: any, state: any) => ({
      ...provided,
      background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)',
      backdropFilter: 'blur(15px)',
      border: state.isFocused 
        ? '2px solid rgba(0, 212, 255, 0.5)' 
        : '1px solid rgba(0, 212, 255, 0.2)',
      borderRadius: '12px',
      boxShadow: state.isFocused 
        ? '0 0 0 0.2rem rgba(0, 212, 255, 0.25)' 
        : 'none',
      minHeight: '48px',
      '&:hover': {
        border: '1px solid rgba(0, 212, 255, 0.4)',
      }
    }),
    placeholder: (provided: any) => ({
      ...provided,
      color: '#6b7280',
      fontSize: '14px'
    }),
    singleValue: (provided: any) => ({
      ...provided,
      color: '#1f2937'
    }),
    menu: (provided: any) => ({
      ...provided,
      background: 'rgba(255, 255, 255, 0.95)',
      backdropFilter: 'blur(10px)',
      border: '1px solid rgba(0, 212, 255, 0.2)',
      borderRadius: '12px',
      overflow: 'hidden',
      boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
      zIndex: 9999
    }),
    menuList: (provided: any) => ({
      ...provided,
      padding: '8px'
    }),
    group: (provided: any) => ({
      ...provided,
      paddingTop: '8px',
      paddingBottom: '8px'
    }),
    groupHeading: (provided: any) => ({
      ...provided,
      background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
      color: '#1f2937',
      fontSize: '12px',
      fontWeight: '600',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      padding: '8px 12px',
      margin: '0 0 4px 0',
      borderRadius: '8px'
    }),
    option: (provided: any, state: any) => ({
      ...provided,
      backgroundColor: 'transparent',
      color: '#1f2937',
      '&:hover': {
        backgroundColor: 'rgba(0, 212, 255, 0.1)'
      }
    }),
    dropdownIndicator: (provided: any) => ({
      ...provided,
      color: '#00d4ff',
      '&:hover': {
        color: '#00d4ff'
      }
    }),
    indicatorSeparator: () => ({
      display: 'none'
    }),
    menuPortal: (provided: any) => ({
      ...provided,
      zIndex: 9999
    })
  };

  return (
    <div 
      className="zip-code-selector"
      style={{
        background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)',
        backdropFilter: 'blur(15px)',
        border: '1px solid rgba(0, 212, 255, 0.2)',
        borderRadius: '16px',
        padding: '20px'
      }}
    >
      <div className="d-flex align-items-center mb-3">
        <FontAwesomeIcon 
          icon={faSearch} 
          style={{ 
            color: '#00d4ff', 
            fontSize: '18px',
            marginRight: '12px',
            filter: 'drop-shadow(0 0 4px rgba(0, 212, 255, 0.3))'
          }} 
        />
        <h6 
          className="mb-0 flex-grow-1" 
          style={{ 
            color: '#1f2937',
            fontWeight: '600'
          }}
        >
          Select Arizona ZIP Code
        </h6>
        
        {showDataAvailability && selectedOption && (
          <div className="d-flex align-items-center">
            <FontAwesomeIcon
              icon={faCircle}
              style={{
                fontSize: '8px',
                color: selectedOption.hasData ? '#10b981' : '#6b7280',
                marginRight: '6px'
              }}
            />
            <span 
              style={{ 
                fontSize: '12px',
                color: selectedOption.hasData ? '#10b981' : '#6b7280',
                fontWeight: '500'
              }}
            >
              {selectedOption.hasData ? 'Data Available' : 'Limited Data'}
            </span>
          </div>
        )}
      </div>

      {loading ? (
        <div 
          className="d-flex align-items-center justify-content-center p-4"
          style={{
            background: 'rgba(255, 255, 255, 0.05)',
            borderRadius: '12px',
            border: '1px solid rgba(0, 212, 255, 0.1)'
          }}
        >
          <FontAwesomeIcon 
            icon={faSpinner} 
            spin 
            className="me-2" 
            style={{ color: '#00d4ff' }} 
          />
          <span style={{ color: '#6b7280' }}>Loading ZIP codes...</span>
        </div>
      ) : error ? (
        <div 
          className="d-flex align-items-center justify-content-center p-4"
          style={{
            background: 'rgba(239, 68, 68, 0.1)',
            borderRadius: '12px',
            border: '1px solid rgba(239, 68, 68, 0.2)'
          }}
        >
          <span style={{ color: '#ef4444' }}>{error}</span>
        </div>
      ) : (
        <Select
          value={selectedOption}
          onChange={(option) => option && onChange(option.value)}
          options={filteredOptions}
          components={{
            Option: CustomOption,
            SingleValue: CustomSingleValue
          }}
          styles={customStyles}
          placeholder={placeholder}
          isDisabled={disabled || loading}
          isSearchable
          isClearable={false}
          onInputChange={setSearchTerm}
          filterOption={() => true} // We handle filtering manually
          menuPlacement="auto"
          maxMenuHeight={300}
          menuPortalTarget={document.body}
          menuShouldScrollIntoView={false}
        />
      )}

      {showDataAvailability && !loading && !error && (
        <div className="mt-3 d-flex justify-content-between text-muted" style={{ fontSize: '12px' }}>
          <div className="d-flex align-items-center">
            <FontAwesomeIcon
              icon={faCircle}
              style={{ fontSize: '8px', color: '#10b981', marginRight: '6px' }}
            />
            <span>Data Available ({zipCodes.filter(z => z.hasData).length})</span>
          </div>
          <div className="d-flex align-items-center">
            <FontAwesomeIcon
              icon={faCircle}
              style={{ fontSize: '8px', color: '#6b7280', marginRight: '6px' }}
            />
            <span>Limited Data ({zipCodes.filter(z => !z.hasData).length})</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default ZipCodeSelector;